import { native } from '@forest/native-services'
import { createEffect, createMemo, createResource, For, Show } from 'solid-js'
import { ChallengeCard } from '~/components/challenge-card'
import { ClockIcon } from '~/components/icons/clock'
import { challengeTitle } from '~/lib/challenge-title'
import { splitSeconds } from '~/lib/countdown'
import { logNonFatal } from '~/lib/diagnostics'
import { getLocalizedMessage } from '~/lib/l10n'
import { appVersion } from '~/lib/params'
import type { PlusTapSurface } from '~/lib/plus-claim'
import {
  decideTreeArrow,
  hasDeeplinkArrowSurface,
  isTreeOwnershipGateActive,
  needsTreeOwnership,
  type TreeOwnershipState,
} from '~/lib/tree-ownership-gate'
import { m } from '~/paraglide/messages'
import type { DailyTask } from '~/resources/focus-challenges'
import { useUnlockedTreesQuery } from '~/resources/unlocked-trees'

type Props = {
  tasks: DailyTask[]
  resetSeconds: number
  loading?: boolean
  claimingId?: number | null
  onTap: (taskIndex: number, surface: PlusTapSurface, event: MouseEvent) => void
}

const SKELETON_SLOTS = [0, 1, 2] as const

export function DailyChallengeCard(props: Props) {
  // Tree-ownership deeplink gate (~/lib/tree-ownership-gate, ADR 0025).
  const gateActive = isTreeOwnershipGateActive(appVersion)
  const unlockedTrees = useUnlockedTreesQuery(() =>
    needsTreeOwnership(props.tasks, gateActive),
  )
  const ownership = (): TreeOwnershipState => {
    // A Set instance is truthy even when empty, so this reads "query has
    // settled with data", not "user owns at least one tree".
    if (unlockedTrees.data) {
      return { status: 'success', unlockedGids: unlockedTrees.data }
    }
    if (unlockedTrees.isError) return { status: 'error' }
    return { status: 'pending' }
  }
  // Once per card per reason: task rows are recreated on every refetch, so
  // dedup lives at component scope rather than in the row effect's closure.
  const reportedHiddenArrows = new Set<string>()

  return (
    <section class="w-full">
      <div class="mx-auto flex w-full max-w-[640px] flex-col gap-[16px] overflow-hidden rounded-[20px] bg-white p-[20px] shadow-[0_4px_20px_-10px_rgba(0,0,0,0.08)]">
        <header class="flex w-full items-start justify-between gap-2">
          <h2 class="min-w-[180px] max-w-[60%] text-[18px] font-semibold leading-[26px] text-ink">
            {m.taskpage_subtitle_dailytask()}
          </h2>
          <span class="flex min-w-0 flex-1 items-start justify-end gap-1 text-xs text-[#888]">
            <ClockIcon class="mt-[1px] size-[14px] shrink-0" />
            <span class="text-right">
              {m.daily_challenge_timer(splitSeconds(props.resetSeconds))}
            </span>
          </span>
        </header>

        <div class="flex w-full flex-col gap-[12px]">
          <Show
            when={!props.loading}
            fallback={
              <For each={SKELETON_SLOTS}>
                {() => (
                  <ChallengeCard
                    title=""
                    current={0}
                    total={1}
                    chestLevel="lv1"
                    chestState="locked"
                    loading
                    onClaimChest={() => {}}
                  />
                )}
              </For>
            }
          >
            <For each={props.tasks}>
              {(task, index) => {
                const i = index()
                const [treeName] = createResource(
                  () => task.progress.challenge_difficulty.tree_type,
                  async (id) => {
                    if (id == null) return null
                    return await getLocalizedMessage(`tree_type_${id}_title`)
                  },
                )
                const deepLink = () =>
                  task.progress.challenge_difficulty.deeplink
                const arrowGate = createMemo(() =>
                  decideTreeArrow({
                    gid: task.progress.challenge_difficulty.tree_type,
                    gateActive,
                    ownership: ownership(),
                  }),
                )
                createEffect(() => {
                  const decision = arrowGate()
                  if (decision.visible) return
                  // Only report when the gate is what hides the arrow — a
                  // card with no deeplink or a non-locked chest never shows
                  // one anyway. Pending is transient, not diagnostic.
                  if (decision.reason === 'ownership_pending') return
                  if (!hasDeeplinkArrowSurface(task)) return
                  const key = `${task.progress.id}:${decision.reason}`
                  if (reportedHiddenArrows.has(key)) return
                  reportedHiddenArrows.add(key)
                  logNonFatal('focus_challenge_tree_deeplink_hidden', {
                    reason: decision.reason,
                    gid: String(task.progress.challenge_difficulty.tree_type),
                    difficulty_id: String(
                      task.progress.challenge_difficulty.id,
                    ),
                  })
                })
                return (
                  <ChallengeCard
                    title={challengeTitle(task.progress, treeName() ?? null)}
                    current={task.progress.progress}
                    total={task.progress.target}
                    chestLevel={task.chestLevel}
                    chestState={task.chestState}
                    plus={task.requiresSubscription}
                    showArrow={
                      hasDeeplinkArrowSurface(task) && arrowGate().visible
                    }
                    onArrowClick={() => {
                      const url = deepLink()
                      if (url) native.general.openURL(url)
                    }}
                    claiming={
                      props.claimingId != null &&
                      props.claimingId === task.progress.id
                    }
                    onCardTap={(e) => props.onTap(i, 'body', e)}
                    onClaimChest={(e) => props.onTap(i, 'chest', e)}
                  />
                )
              }}
            </For>
          </Show>
        </div>
      </div>
    </section>
  )
}
